import { PlusOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { Button } from '../../../../components/button';
import { Section } from '../../../../components/form-fields/section';
import {
    PromotionAvailability,
    PromotionForm,
} from '../../../../stores/promotion';
import { AvailabilityCard } from './AvailabilityCard';
import { EmptyAvailabilities } from './EmptyAvailabilities';
import style from './style.module.scss';

type NewAvailability = Omit<PromotionAvailability, 'id'>;

// Pure functions for availability operations
function createNewAvailability(
    startDate: Date | null,
    endDate: Date | null,
    currentCount: number
): PromotionAvailability {
    const newAvailability: NewAvailability = {
        dayOfWeek: 3,
        startHour: 9,
        endHour: 18,
        dailyLimit: 0,
        startDate: startDate,
        endDate: endDate,
    };

    const tempId = -1 * (currentCount + 1);
    return {
        ...newAvailability,
        id: tempId,
    } as PromotionAvailability;
}

function normalizeDate(date: Date | null): Date | null {
    if (!date) return null;
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0);
}

interface AvailabilitiesSectionProps {
    values: PromotionForm;
    setFieldValue: (field: string, value: unknown) => void;
    checkIfHasError: (path: string) => boolean;
}

export const AvailabilitiesSection: React.FC<AvailabilitiesSectionProps> = ({
    values,
    setFieldValue,
}) => {
    // Component state for managing availabilities
    const [availabilities, setAvailabilities] = useState<
        PromotionAvailability[]
    >([]);
    const [idsToDelete, setIdsToDelete] = useState<number[]>([]);

    useEffect(() => {
        // Initialize component state from form values only once when data is available
        const shouldInitialize =
            (values.availabilities?.length ?? 0) > 0 &&
            availabilities.length === 0;
        if (shouldInitialize && values.availabilities) {
            setAvailabilities(values.availabilities);
        }

        // Handle availability IDs to delete
        if ((values.availabilityIdsToDelete?.length ?? 0) > 0) {
            setIdsToDelete(
                values.availabilityIdsToDelete?.filter(
                    (id: number | undefined): id is number => id !== null
                ) ?? []
            );
        }
    }, [
        values.availabilities,
        values.availabilityIdsToDelete,
        availabilities.length,
    ]);

    const addAvailability = () => {
        const startDate = normalizeDate(values.startDate);
        const endDate = normalizeDate(values.endDate);
        const newAvailability = createNewAvailability(
            startDate,
            endDate,
            availabilities.length
        );
        const newAvailabilities = [...availabilities, newAvailability];
        setAvailabilities(newAvailabilities);
        setFieldValue('availabilities', newAvailabilities);
    };

    const removeAvailability = (index: number) => {
        const availabilityToRemove = availabilities[index];
        const idToDelete =
            availabilityToRemove.id && availabilityToRemove.id > 0
                ? availabilityToRemove.id
                : null;

        if (idToDelete !== null) {
            const newIdsToDelete = [...idsToDelete, idToDelete];
            setIdsToDelete(newIdsToDelete);
            setFieldValue('availabilityIdsToDelete', newIdsToDelete);
        }

        const newAvailabilities = availabilities.filter((_, i) => i !== index);
        setAvailabilities(newAvailabilities);
        setFieldValue('availabilities', newAvailabilities);
    };

    const updateAvailability = (
        index: number,
        field: keyof PromotionAvailability,
        value: string | number | Date | null
    ) => {
        const newAvailabilities = availabilities.map((a, i) =>
            i === index ? { ...a, [field]: value } : a
        );
        setAvailabilities(newAvailabilities);
        setFieldValue('availabilities', newAvailabilities);
    };

    return (
        <Section title="Availabilities">
            {availabilities.length === 0 ? (
                <EmptyAvailabilities onAdd={addAvailability} />
            ) : (
                <>
                    {/* For each availability, render Frame an index number and an AvailabilityCard */}
                    {availabilities.map((availability, index) => (
                        <div
                            key={`availability-${index}`}
                            className={style.availability_row}
                        >
                            <span className={style.index_number}>
                                {/* Prefix with 0 to ensure 2 digits */}
                                {(index + 1).toString().padStart(2, '0')}
                            </span>
                            <AvailabilityCard
                                availability={availability}
                                values={values}
                                updateAvailability={(field, value) =>
                                    updateAvailability(index, field, value)
                                }
                                removeAvailability={() =>
                                    removeAvailability(index)
                                }
                            />
                        </div>
                    ))}
                    <div className={style.add_button_container}>
                        <Button
                            variant="secondary"
                            onClick={addAvailability}
                            icon={<PlusOutlined />}
                        >
                            Add availability
                        </Button>
                    </div>
                </>
            )}
        </Section>
    );
};

export default AvailabilitiesSection;
